import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { percent, type AnalyticsSeries } from "../analytics";
import { DataTable, downloadCsv } from "../components/DataTable";
import { LineChart, type LineSeries } from "../components/LineChart";
import { bucketHeader, bucketLabeller, perWord, RangeControls } from "../components/RangeControls";
import { ShareTable, type ShareRow } from "../components/ShareTable";
import { dayLabel } from "../earlyAccess";
import { total } from "../features";
import { latestDay, rangeDays, seriesFor, type Bucket, type RangeId } from "../growth";
import {
  byVersion,
  latestSplit,
  OVERFLOW,
  PUSH_CHANNELS,
  PUSH_RESULTS,
  sharePercent,
  sumWhere,
  topSeries,
  type Split,
} from "../platforms";
import { errorText } from "../problem";
import { readSeries } from "../seriesApi";

// Each platform keeps the colour it has on the Growth page.
const PLATFORMS = [
  { id: "android", label: "Android", tone: "series-1" },
  { id: "web", label: "Web", tone: "series-2" },
  { id: "ios", label: "iPhone", tone: "series-5" },
] as const;
const PERMISSIONS = [
  { id: "always_precise", label: "Always, precise", tone: "series-1" },
  { id: "always_approximate", label: "Always, approximate", tone: "series-2" },
  { id: "foreground_only", label: "Only while in use", tone: "series-3" },
  { id: "denied", label: "Denied", tone: "series-4" },
  { id: "unknown", label: "Not reported", tone: "muted" },
] as const;
const SWITCH = [
  { id: "on", label: "On" },
  { id: "off", label: "Off" },
  { id: "unknown", label: "Not reported" },
] as const;
const VERSION_TONES = ["series-1", "series-2", "series-3", "series-4"] as const;
const TOP_VERSIONS = VERSION_TONES.length;

const labelOf = (list: readonly { id: string; label: string }[], key: string) =>
  list.find((item) => item.id === key)?.label ?? key;
const pct = (value: number) => `${Number.isInteger(value) ? value : value.toFixed(1)}%`;

/** The page's reads, together, so a range or bucket change never mixes old and new numbers. */
function usePlatforms(from: string, to: string, bucket: Bucket) {
  return useQuery({
    queryKey: ["analytics-platforms", from, to, bucket],
    queryFn: async () => {
      const [devices, appVersions, osVersions, permission, locationSwitch, registered, pushes] = await Promise.all([
        readSeries("devices", from, to, bucket, { group: "platform" }),
        readSeries("devices", from, to, bucket, { group: "app_version", filter: "platform:android" }),
        readSeries("device_os", from, to, bucket, { group: "os_version", filter: "platform:android" }),
        readSeries("location_permission", from, to, bucket, { group: "permission" }),
        readSeries("location_switch", from, to, bucket, { group: "state" }),
        readSeries("push_registered", from, to, bucket, { group: "platform,registered" }),
        readSeries("pushes", from, to, bucket, { group: "channel,result" }),
      ]);
      return { from, to, bucket, devices, appVersions, osVersions, permission, locationSwitch, registered, pushes };
    },
    placeholderData: keepPreviousData,
    staleTime: 10 * 60_000,
  });
}

export function PlatformsPage() {
  const [range, setRange] = useState<RangeId>("90d");
  const [bucket, setBucket] = useState<Bucket>("week");
  const days = rangeDays(range, bucket, latestDay());
  const query = usePlatforms(days.from, days.to, bucket);
  const data = query.data;
  return (
    <>
      <h1>Platforms</h1>
      <section className="stack">
        <RangeControls range={range} bucket={bucket} onRange={setRange} onBucket={setBucket} />
        {query.isError ? (
          <p className="error">{errorText(query.error)}</p>
        ) : !data ? (
          <p>Loading…</p>
        ) : (
          <PlatformCharts data={data} />
        )}
      </section>
    </>
  );
}

type Data = NonNullable<ReturnType<typeof usePlatforms>["data"]>;

const shareRows = (parts: readonly Split[], label: (key: string) => string): ShareRow[] =>
  parts.map((part) => ({ key: part.key, label: label(part.key), value: part.value, share: part.share }));
const asOf = (split: { bucket: string; total: number } | null, what: string) =>
  split ? `${split.total.toLocaleString()} ${what} in the bucket from ${dayLabel(split.bucket)}` : "not counted yet";

function PlatformCharts({ data }: { data: Data }) {
  const bucketLabel = bucketLabeller(data.bucket);
  const per = perWord(data.bucket);
  const buckets = data.devices.buckets;
  const file = (name: string) => `ten20-${name}-${data.from}-${data.to}-${data.bucket}.csv`;

  const platforms = latestSplit(data.devices, "platform");
  const platformSeries: LineSeries[] = PLATFORMS.map((p) => ({
    key: p.id,
    label: p.label,
    tone: p.tone,
    values: seriesFor(data.devices, "platform", p.id),
  }));

  const versionLabel = (key: string) =>
    key === "unknown" ? "Not reported" : key === "other" ? "Unrecognised" : key === OVERFLOW ? "More versions" : key;
  const versionChart = (read: AnalyticsSeries, dim: string, rest: string): LineSeries[] => {
    const { top, rest: others } = topSeries(read, dim, TOP_VERSIONS);
    return [
      ...top.map((s, i) => ({ key: s.key, label: versionLabel(s.key), tone: VERSION_TONES[i]!, values: s.values })),
      ...(others ? [{ key: "rest", label: rest, tone: "muted" as const, values: others }] : []),
    ];
  };
  const apps = latestSplit(data.appVersions, "app_version");
  const appSeries = versionChart(data.appVersions, "app_version", "Other versions");
  const oses = latestSplit(data.osVersions, "os_version");
  const osSeries = versionChart(data.osVersions, "os_version", "Other versions");

  const permissions = latestSplit(data.permission, "permission");
  const permissionSeries: LineSeries[] = PERMISSIONS.map((p) => ({
    key: p.id,
    label: p.label,
    tone: p.tone,
    values: sharePercent(
      data.permission,
      () => true,
      (d) => d.permission === p.id,
    ),
  }));
  const switches = latestSplit(data.locationSwitch, "state");
  const offSeries: LineSeries[] = [
    {
      key: "off",
      label: "Location off",
      tone: "series-4",
      values: sharePercent(
        data.locationSwitch,
        () => true,
        (d) => d.state === "off",
      ),
    },
  ];

  const registeredSeries: LineSeries[] = PLATFORMS.map((p) => ({
    key: p.id,
    label: p.label,
    tone: p.tone,
    values: sharePercent(
      data.registered,
      (d) => d.platform === p.id,
      (d) => d.registered === "true",
    ),
  }));
  const registeredRows: ShareRow[] = (() => {
    const split = latestSplit(data.registered, "platform");
    if (!split) return [];
    return PLATFORMS.flatMap((p) => {
      const index = buckets.indexOf(split.bucket);
      const of = (yes: boolean) =>
        data.registered.series
          .filter((s) => s.dims?.platform === p.id && (s.dims?.registered === "true") === yes)
          .reduce((sum, s) => sum + (s.values[index] ?? 0), 0);
      const [registered, not] = [of(true), of(false)];
      return registered + not > 0
        ? [{ key: p.id, label: p.label, value: registered, share: registered / (registered + not) }]
        : [];
    });
  })();

  const pushRate: LineSeries[] = PUSH_CHANNELS.map((channel, i) => ({
    key: channel.id,
    label: channel.label,
    tone: i === 0 ? "series-1" : "series-2",
    values: sharePercent(
      data.pushes,
      (d) => d.channel === channel.id,
      (d) => d.result === "sent",
    ),
  }));
  const pushTotals = PUSH_CHANNELS.map((channel) => {
    const results = PUSH_RESULTS.map(
      (result) => total(sumWhere(data.pushes, (d) => d.channel === channel.id && d.result === result)) ?? 0,
    );
    const all = results.reduce((a, b) => a + b, 0);
    return { channel, results, all, rate: all > 0 ? results[0]! / all : null };
  });

  return (
    <>
      <p className="muted">
        UTC days from {dayLabel(data.from)} through {dayLabel(data.to)}
        {data.pushes.built_through
          ? `; the rollup has built through ${dayLabel(data.pushes.built_through)}`
          : "; the rollup has built nothing yet"}
        . Devices are those seen in the 30 days up to each day, counted when the rollup builds its latest day (a bucket
        shows its last such day); versions are as the apps report them.
      </p>
      <h2>Devices</h2>
      <div className="charts">
        <ShareTable
          title="Devices in use by platform"
          note={asOf(platforms, "devices")}
          header={["Platform", "Devices"]}
          rows={shareRows(
            platforms ? PLATFORMS.flatMap((p) => platforms.parts.filter((part) => part.key === p.id)) : [],
            (key) => labelOf(PLATFORMS, key),
          )}
          fileName={file("platforms")}
        />
        <LineChart
          title={`Devices in use per ${per}, by platform`}
          buckets={buckets}
          bucketLabel={bucketLabel}
          series={platformSeries}
        />
      </div>
      <h2>Android versions</h2>
      <div className="charts">
        <ShareTable
          title="Android app versions"
          note={asOf(apps, "Android devices")}
          header={["App version", "Devices"]}
          rows={shareRows(apps ? byVersion(apps.parts) : [], versionLabel)}
          fileName={file("app-versions")}
        />
        <LineChart
          title={`Android app versions per ${per}`}
          note={`the ${TOP_VERSIONS} most used`}
          buckets={buckets}
          bucketLabel={bucketLabel}
          series={appSeries}
        />
        <ShareTable
          title="Android releases"
          note={asOf(oses, "Android devices")}
          header={["Android", "Devices"]}
          rows={shareRows(oses ? byVersion(oses.parts) : [], versionLabel)}
          fileName={file("android-releases")}
        />
        <LineChart
          title={`Android releases per ${per}`}
          note={`the ${TOP_VERSIONS} most used`}
          buckets={buckets}
          bucketLabel={bucketLabel}
          series={osSeries}
        />
      </div>
      <h2>Location (Android)</h2>
      <div className="charts">
        <ShareTable
          title="Location permission"
          note={asOf(permissions, "Android devices")}
          header={["Permission", "Devices"]}
          rows={shareRows(
            permissions ? PERMISSIONS.flatMap((p) => permissions.parts.filter((part) => part.key === p.id)) : [],
            (key) => labelOf(PERMISSIONS, key),
          )}
          fileName={file("location-permission")}
        />
        <LineChart
          title={`Location permission shares per ${per}`}
          buckets={buckets}
          bucketLabel={bucketLabel}
          format={pct}
          series={permissionSeries}
        />
        <ShareTable
          title="Location switch"
          note={asOf(switches, "Android devices")}
          header={["System location", "Devices"]}
          rows={shareRows(
            switches ? SWITCH.flatMap((s) => switches.parts.filter((part) => part.key === s.id)) : [],
            (key) => labelOf(SWITCH, key),
          )}
          fileName={file("location-switch")}
        />
        <LineChart
          title={`Devices with location off per ${per}`}
          note="share of Android devices"
          buckets={buckets}
          bucketLabel={bucketLabel}
          format={pct}
          series={offSeries}
        />
      </div>
      <h2>Push</h2>
      <div className="charts">
        <ShareTable
          title="Push registered"
          note="share of each platform's devices"
          header={["Platform", "Registered"]}
          rows={registeredRows}
          fileName={file("push-registered")}
        />
        <LineChart
          title={`Push registered per ${per}`}
          note="share of each platform's devices"
          buckets={buckets}
          bucketLabel={bucketLabel}
          format={pct}
          series={registeredSeries}
        />
        <PushTotals rows={pushTotals} fileName={file("push-results")} />
        <LineChart
          title={`Push success per ${per}`}
          note="sent of all attempts"
          buckets={buckets}
          bucketLabel={bucketLabel}
          format={pct}
          series={pushRate}
        />
      </div>
      <DataTable
        header={[
          bucketHeader(data.bucket),
          ...platformSeries.map((s) => `Devices: ${s.label}`),
          ...appSeries.map((s) => `App version: ${s.label}`),
          ...osSeries.map((s) => `Android: ${s.label}`),
          ...permissionSeries.map((s) => `Permission (%): ${s.label}`),
          "Location off (%)",
          ...registeredSeries.map((s) => `Push registered (%): ${s.label}`),
          ...pushRate.map((s) => `Push success (%): ${s.label}`),
        ]}
        rows={buckets
          .map((start, i) => [
            start,
            ...[
              ...platformSeries,
              ...appSeries,
              ...osSeries,
              ...permissionSeries,
              ...offSeries,
              ...registeredSeries,
              ...pushRate,
            ].map((s) => s.values[i] ?? null),
          ])
          .toReversed()}
        fileName={file("platforms-all")}
      />
    </>
  );
}

function PushTotals({
  rows,
  fileName,
}: {
  rows: {
    channel: (typeof PUSH_CHANNELS)[number];
    results: number[];
    all: number;
    rate: number | null;
  }[];
  fileName: string;
}) {
  const header = ["Channel", ...PUSH_RESULTS.map((r) => r[0]!.toUpperCase() + r.slice(1)), "Success"];
  return (
    <figure className="chart">
      <figcaption>
        Push attempts in the range <span className="muted">· invalid registrations are dropped</span>
      </figcaption>
      <table>
        <thead>
          <tr>
            {header.map((name, i) => (
              <th key={name} className={i === 0 ? undefined : "num"}>
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.channel.id}>
              <td>{row.channel.label}</td>
              {row.results.map((value, i) => (
                <td key={PUSH_RESULTS[i]} className="num">
                  {value.toLocaleString()}
                </td>
              ))}
              <td className="num">{percent(row.rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        <button
          type="button"
          className="button-quiet"
          onClick={() =>
            downloadCsv(
              fileName,
              [...header.slice(0, -1), "Success (%)"],
              rows.map((row) => [
                row.channel.label,
                ...row.results,
                row.rate === null ? null : Math.round(row.rate * 1000) / 10,
              ]),
            )
          }
        >
          Download CSV
        </button>
      </p>
    </figure>
  );
}
